import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Search, Home, Users, User as UserIcon, LogIn,
  Bell, Settings, LogOut
} from 'lucide-react';
import NotificationPanel from './NotificationPanel';
import PrivacyCookiesModal from './PrivacyCookiesModal';

const MERCOR_PURPLE = '#5B3DC5';
const MERCOR_GRAY = '#9CA3AF';

/* Custom Earnings icon matching Mercor */
const EarningsIcon = ({ className, ...props }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M12 8v8" />
    <path d="M14.5 10h-3.5a1.5 1.5 0 0 0 0 3h2a1.5 1.5 0 0 1 0 3h-3.5" />
  </svg>
);

/* Custom referral horn icon */
const ReferralHornIcon = ({ className, ...props }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M3 11l18-5v12L3 13v-2z" />
    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
  </svg>
);

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const hasAccepted = localStorage.getItem('mercor_privacy_accepted');
    if (!hasAccepted) setPrivacyOpen(true);
  }, [user]);

  const handlePrivacyConfirm = (prefs) => {
    localStorage.setItem('mercor_privacy_accepted', JSON.stringify(prefs));
    setPrivacyOpen(false);
  };

  const mainMenuItems = [
    { icon: Search, label: 'Explore', path: '/explore' },
    { icon: Home, label: 'Home', path: '/home' },
    { icon: Users, label: 'Referrals', path: '/referrals' },
    { icon: EarningsIcon, label: 'Earnings', path: '/earnings' },
    { icon: UserIcon, label: 'Profile', path: '/profile' },
  ];

  const handleNavigation = (path) => navigate(path);
  const handleLogout = () => { logout(); navigate('/'); };

  /* Desktop sidebar item - exact Mercor style */
  const renderIconItem = (item) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    return (
      <div key={item.path} className="relative w-full">
        {isActive && (
          <div
            className="absolute left-0 top-1/2 -translate-y-1/2 rounded-r-full"
            style={{ backgroundColor: MERCOR_PURPLE, width: '3px', height: '28px' }}
          />
        )}
        <button
          onClick={() => handleNavigation(item.path)}
          className="flex flex-col items-center justify-center gap-[2px] w-full transition-all relative"
          style={{ 
            color: isActive ? MERCOR_PURPLE : MERCOR_GRAY,
            height: '56px',
            backgroundColor: isActive ? '#EDE9FE' : 'transparent',
          }}
          title={item.label}
          onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = '#4B5563'; }}
          onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = MERCOR_GRAY; }}
        >
          <Icon style={{ width: '22px', height: '22px' }} strokeWidth={isActive ? 2 : 1.5} />
          <span style={{ fontSize: '10px', lineHeight: '12px', fontWeight: isActive ? 600 : 500 }}>
            {item.label}
          </span>
        </button>
      </div>
    );
  };

  return (
    <>
      {/* MOBILE: Top Header Bar */}
      <div
        className="md:hidden fixed top-0 left-0 right-0 z-50 flex items-center justify-between"
        style={{
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E5E7EB',
          height: '56px',
          padding: '0 16px',
        }}
      >
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
          <span style={{ fontSize: '24px', fontWeight: 900, color: MERCOR_PURPLE, lineHeight: 1 }}>M</span>
        </div>
        {user ? (
          <button
            onClick={() => navigate('/profile')}
            className="rounded-full flex items-center justify-center shrink-0 overflow-hidden"
            style={{ width: '36px', height: '36px', backgroundColor: '#EDE9FE' }}
          >
            {user.avatar ? (
              <img src={user.avatar} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <UserIcon style={{ width: '18px', height: '18px', color: MERCOR_PURPLE }} />
            )}
          </button>
        ) : (
          <button
            onClick={() => navigate('/login')}
            className="rounded-full flex items-center justify-center shrink-0"
            style={{ width: '36px', height: '36px', backgroundColor: '#F3F4F6' }}
          >
            <UserIcon style={{ width: '18px', height: '18px', color: MERCOR_GRAY }} />
          </button>
        )}
      </div>

      {/* MOBILE: Bottom Navigation Bar */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-50"
        style={{
          backgroundColor: '#FFFFFF',
          borderTop: '1px solid #E5E7EB',
        }}
      >
        <nav className="flex items-center justify-around" style={{ height: '56px', padding: '0 4px' }}>
          {mainMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className="flex flex-col items-center justify-center gap-[2px] transition-all"
                style={{ color: isActive ? MERCOR_PURPLE : MERCOR_GRAY, minWidth: '56px', height: '48px' }}
              >
                <Icon style={{ width: '22px', height: '22px' }} strokeWidth={isActive ? 2 : 1.5} />
                <span style={{ fontSize: '10px', lineHeight: '12px', fontWeight: isActive ? 600 : 500 }}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* DESKTOP: Sidebar - exact 60px wide like Mercor */}
      <div
        className="hidden md:flex fixed left-0 top-0 h-screen flex-col z-40"
        style={{
          backgroundColor: '#FFFFFF',
          borderRight: '1px solid #E5E7EB',
          width: '60px',
        }}
      >
        {/* Logo - Bold M */}
        <div className="flex items-center justify-center" style={{ height: '56px' }}>
          <div className="cursor-pointer" onClick={() => navigate('/')}>
            <span style={{ fontSize: '28px', fontWeight: 900, color: MERCOR_PURPLE, lineHeight: 1 }}>M</span>
          </div>
        </div>

        {/* Main nav items */}
        <nav className="flex-1 flex flex-col items-center" style={{ marginTop: '4px' }}>
          {mainMenuItems.map(item => renderIconItem(item))}
        </nav>

        {/* Bottom icons */}
        <div className="flex flex-col items-center" style={{ paddingBottom: '12px' }}>
          <button
            onClick={() => navigate('/settings')}
            className="flex flex-col items-center justify-center w-full transition-all"
            style={{ color: location.pathname === '/settings' ? MERCOR_PURPLE : MERCOR_GRAY, height: '48px' }}
            title="Settings"
          >
            <Settings style={{ width: '20px', height: '20px' }} strokeWidth={1.5} />
          </button>

          <button
            onClick={() => setNotificationOpen(!notificationOpen)}
            className="flex flex-col items-center justify-center w-full transition-all relative"
            style={{ color: notificationOpen ? MERCOR_PURPLE : MERCOR_GRAY, height: '48px' }}
            title="Notifications"
          >
            <div className="relative">
              <Bell style={{ width: '20px', height: '20px' }} strokeWidth={1.5} />
              <div className="absolute rounded-full" style={{ top: '-2px', right: '-2px', width: '8px', height: '8px', backgroundColor: '#EF4444', border: '1.5px solid #FFFFFF' }} />
            </div>
          </button>

          <button
            onClick={() => navigate('/referrals')}
            className="flex flex-col items-center justify-center w-full transition-all"
            style={{ color: '#EF4444', height: '48px' }}
            title="Referrals"
          >
            <ReferralHornIcon style={{ width: '20px', height: '20px' }} />
          </button>

          {user ? (
            <button
              onClick={handleLogout}
              className="flex flex-col items-center justify-center gap-[2px] transition-colors"
              style={{ color: MERCOR_GRAY, height: '48px' }}
              title="Sign out"
            >
              <LogOut style={{ width: '20px', height: '20px' }} strokeWidth={1.5} />
              <span style={{ fontSize: '9px', fontWeight: 500 }}>Sign out</span>
            </button>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className="flex flex-col items-center justify-center gap-[2px] transition-colors"
              style={{ color: MERCOR_GRAY, height: '48px' }}
              title="Sign in"
            >
              <LogIn style={{ width: '20px', height: '20px' }} strokeWidth={1.5} />
              <span style={{ fontSize: '9px', fontWeight: 500 }}>Sign in</span>
            </button>
          )}
        </div>
      </div>

      <NotificationPanel isOpen={notificationOpen} onClose={() => setNotificationOpen(false)} />
      <PrivacyCookiesModal
        isOpen={privacyOpen}
        onClose={() => { localStorage.setItem('mercor_privacy_accepted', 'true'); setPrivacyOpen(false); }}
        onConfirm={handlePrivacyConfirm}
      />
    </>
  );
};

export default Sidebar;
